import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { Typography, TextareaAutosize } from "@mui/material";
import styled from "styled-components";
import { Box } from "@mui/system";
import MuiTab from "./Tab/MuiTab";
import CommentsBox from "./comments/CommentsBox";
import useToaster from "../customHook/useToaster";
import useSafeInput from "../customHook/useSafeInput";
import { CHAR_LIMIT } from "../constants/utils";

const tabs = [{ label: "View Comment" }, { label: "Add New Comment" }];

const ThemedTextareaAutosize = styled(TextareaAutosize)(
  () => `
  resize: none;
  width: 100%;
  padding: 0.6rem;
  border-radius: 4px;
  border: 1px solid #E8E8E8;
  font-family: inter;
  font-size: 1.4rem;
  line-height: 1.5;
`
);

const CommonDescription = ({
  minRows = 9,
  isHeadingHidden = false,
  heading = "Description",
  placeholder = "Enter Description",
  onSubmitButton = () => {},
  onChange = () => {},
  descriptionVal = "",
  descriptionRef,
  isButtonHidden = false,
  disabled = false,
  bgcolor = "#fff",
  isTabView = false,
  tabData = [],
  isFromHoldUnhold = false,
  limit,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { showToast } = useToaster();

  //  HOOK USE
  const safeInput = useSafeInput({
    value: descriptionVal || "",
    onChange,
    showToast,
    limit: limit !== undefined ? limit : isFromHoldUnhold ? 250 : CHAR_LIMIT,
  });

  const hasLimit =
    typeof (limit !== undefined
      ? limit
      : isFromHoldUnhold
        ? 250
        : CHAR_LIMIT) === "number";
  const displayLimit =
    limit !== undefined ? limit : isFromHoldUnhold ? 250 : CHAR_LIMIT;

  // Focus on tab switch
  useEffect(() => {
    safeInput.setValue("");
    if (tabs[activeTab]?.label === "Add New Comment") {
      document.getElementById("case-desc-input")?.focus();
    }
  }, [activeTab]);

  const handleSubmit = (e) => {
    if (!safeInput.value.trim()) {
      showToast("Please fill description", "info", true);
      return;
    }

    onSubmitButton(e);

    // clear after submit
    safeInput.setValue("");
    onChange({ target: { value: "" } });
  };

  useEffect(() => {
    safeInput.setValue(descriptionVal || "");
  }, [descriptionVal]);

  return (
    <Box
      sx={
        isTabView
          ? {
              boxShadow: "0px 2px 4px 4px #A39D9933",
              marginTop: "1.3rem",
              padding: "12px",
              borderRadius: "12px",
            }
          : { marginTop: "1.5rem" }
      }
    >
      {/* Heading */}
      <Typography
        sx={{
          marginBottom: "1.2rem",
          color: "#000",
          fontSize: "1.3rem",
          display: isHeadingHidden ? "none" : "block",
          fontWeight: 600,
        }}
      >
        {heading}
      </Typography>

      {/* Tabs */}
      {isTabView && (
        <MuiTab
          tabs={tabs}
          variant="standard"
          fontSize="12px"
          standardMinContent
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}

      {isTabView && <Box sx={{ p: 1.5 }} />}

      {/* View Comments */}
      {tabs[activeTab]?.label === "View Comment" && isTabView && (
        <CommentsBox sidebarTable data={tabData} />
      )}

      {/* Add Comment */}
      {(tabs[activeTab]?.label === "Add New Comment" || !isTabView) && (
        <Box>
          <Box position="relative" width="100%">
            <ThemedTextareaAutosize
              id="case-desc-input"
              disabled={disabled}
              minRows={minRows}
              maxRows={10}
              placeholder={placeholder}
              ref={descriptionRef}
              style={{
                backgroundColor: bgcolor,
                overflowY: "auto",
                resize: "none",
              }}
              {...safeInput.bind}
            />

            {/* Counter */}
            <Typography
              sx={{
                fontSize: "1.1rem",
                color:
                  hasLimit && safeInput.count >= displayLimit
                    ? "#d32f2f"
                    : "#9e9e9e",
                textAlign: "right",
                marginTop: "0.4rem",
              }}
            >
              {safeInput.count}
              {hasLimit ? ` / ${displayLimit}` : null}
            </Typography>
          </Box>

          {/* Submit Button */}
          {!isButtonHidden && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1.7rem",
                marginBottom: "1.7rem",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  ":hover": {
                    bgcolor:
                      safeInput.count > 0
                        ? "#e85055"
                        : "rgba(232, 80, 91, 0.3)",
                    color: safeInput.count > 0 ? "white" : "#E8505B",
                  },
                  width: "max-content",
                  textTransform: "none",
                  backgroundColor:
                    safeInput.count > 0 ? "#e85055" : "rgba(232, 80, 91, 0.3)",
                  fontSize: "1.2rem",
                  padding: "0.8rem 2.7rem",
                  boxShadow: "4px 4px 3px rgba(0, 0, 0, 0.25)",
                  color: safeInput.count > 0 ? "white" : "#E8505B",
                }}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </div>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CommonDescription;
